// M005-F0064 (M005-D005 §9e, amendment 2026-09-08) — the shared booking
// detail drawer primitive.
//
// Clicking a booking on ANY calendar surface opens ONE drawer — one
// component, reused, not per-page copies (§9e). This is !350's
// BookingsCalendar drawer lifted out of that file unchanged in DOM, class
// vocabulary (bkh-drawer-*) and testids, so the same backdrop/aside dialog
// pattern AdminComponents.js (user-detail-drawer) and TeacherComponents.js
// (term-booking-drawer) already use is preserved — no second drawer
// convention. BookingsCalendar.js now mounts THIS component; the teacher
// Bookings tab (M005-F0061) and the hub Day mode (M005-F0063) mount the
// same one from their stacking lanes.
//
// The price-plan selector is a NAMED SLOT, not shipped UI: `planSelectorSlot`
// is whatever node the caller passes (the F0065 plan-selector tab family in
// the stacking lane), rendered inside the gated region below ONLY when the
// SELECTED organisation's my-permissions includes a pricing key —
// hasPlanSelectorPermission, permission-derived, never a role name. The gate
// lives here as the single enforcement point; the slot content never
// re-checks permissions. Until the stacking lane passes content, nothing
// renders for anyone — an honest visible gap, not a placeholder selector.
import React, { useRef, useState } from 'react';
import BookingDrawerTabs from './BookingDrawerTabs';
import {
  TAB_BOOKING, initialDrawerTab, rememberTab,
} from './bookingDrawerTabState';
import { BookingTxBillingTab, BookingTxHistoryTab, useBookingTxHistory } from './BookingTxPanels';
import useDismissOnOutsideClick from '../../hooks/useDismissOnOutsideClick';
import { formatSingleBookingWhen } from './bookingsGrouping';
import { hasPlanSelectorPermission } from './bookingsHubPermissions';
import { isBookingPriceSectionBackendAvailable } from '../../utils/priceFeatureFlags';
import { occurrenceDateOf } from '../../utils/priceWire';
import { isPendingDisplayStatus, requesterNameOf } from './studioBookingsView';
import { formatDateForLocalInput } from '../../utils/localDateTimeInput';
import { bookingApi } from '../../services/api';
import { isCalendarGuestIdentityEligible } from './calendarGuestEligibility';
import StepUpDialog from '../auth/StepUpDialog';
import { getStepUpChallengeFromError } from '../../utils/stepUp';
import TxChip from '../TxChip';
import './BookingDrawer.css';

// Lazy: the Price section (and the API client it pulls in) is only loaded when
// its flag is ON, so the shipped drawer's module graph is unchanged.
const BookingPriceSection = React.lazy(() => import('./BookingPriceSection'));

export const BOOKING_DRAWER_TESTID = 'bookings-hub-booking-drawer';
export const PLAN_SLOT_TESTID = 'bkh-drawer-plan-slot';
export const EDIT_TOGGLE_TESTID = 'bkh-drawer-edit-toggle';
export const EDIT_FORM_TESTID = 'bkh-drawer-edit-form';

// M004-F0678 (WebUI #81/#108): what the drawer says in place of a Save button
// while the booking is still pending. Core prices a booking when it is approved
// (cheapest applicable plan, M005-D004 §10.12) and refuses a price write on a
// non-approved booking (409 PRICING_BOOKING_NOT_APPROVED), so a Save here would
// only ever print Core's refusal.
export const PENDING_PRICE_HOLD = 'Approve this booking first. Core prices it on approval (the cheapest plan that applies), and you can change the price here afterwards.';

const hasCanonicalBillingProjection = (booking) => Boolean(booking
  && Object.prototype.hasOwnProperty.call(booking, 'ledger_customer_id'));

const BookingDrawer = ({
  occurrence, onClose, permissions, planSelectorSlot, organisationId, actionsSlot = null, onBillingCustomerResolved = null, enableGuestCorrection = false,
  onBookingSaved = null,
  // M004-F0718: Booking / History / Billing tabs ONLY when the host passes `transaction`
  // ({ hand_off_id, tx_id, orgId, initialTab?, bookingUnavailable? }). Without it the drawer is
  // byte-identical to what it was (Studio stays untouched until Core N2 lands).
  transaction = null,
}) => {
  const drawerDismissHandlers = useDismissOnOutsideClick(onClose);
  // M004-F0712 S3: the hand_off_id the Price read reports, keyed by the booking+occurrence it belongs to (never shown for another booking).
  const [priceHandOff, setPriceHandOff] = useState({ key: '', id: '' });
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [editSaved, setEditSaved] = useState(false);
  const [guestEmail, setGuestEmail] = useState('');
  const [guestCorrectionError, setGuestCorrectionError] = useState('');
  const [guestCorrectionSaving, setGuestCorrectionSaving] = useState(false);
  const [guestCorrectionOpen, setGuestCorrectionOpen] = useState(false);
  const [guestCorrectionStepUp, setGuestCorrectionStepUp] = useState(null);
  const guestCorrectionReplay = useRef(null);
  const hasTx = Boolean(transaction && transaction.hand_off_id);
  const [tab, setTab] = useState(() => (hasTx ? initialDrawerTab(transaction.initialTab) : TAB_BOOKING));
  // ONE history read for both tabs, made lazily the first time a tab other than Booking is selected.
  const txRead = useBookingTxHistory({
    orgId: transaction?.orgId ?? organisationId,
    handOffId: hasTx ? transaction.hand_off_id : null,
    enabled: hasTx && tab !== TAB_BOOKING,
  });
  const selectTab = (key) => { setTab(key); rememberTab(key); };

  if (!occurrence) {
    return null;
  }

  const booking = occurrence.sourceBooking || {};
  const timeLabel = formatSingleBookingWhen(occurrence.start, occurrence.end);
  const showPlanSlot = Boolean(planSelectorSlot) && hasPlanSelectorPermission(permissions);
  const calendarGuestEligible = isCalendarGuestIdentityEligible(booking);
  const calendarGuestUnresolved = enableGuestCorrection && calendarGuestEligible
    && booking.ledger_customer_id == null;
  const projectionAvailable = hasCanonicalBillingProjection(booking);
  const canCorrectGuest = enableGuestCorrection && projectionAvailable
    && permissions?.bookings_manage_all === true && (occurrence.bookingId ?? booking.id) != null;
  const showGuestCorrectionForm = canCorrectGuest && (calendarGuestUnresolved || guestCorrectionOpen);
  // M004-F0648: the Price section (design §3.6). Build-time flag, default OFF
  // until Core M005-F0083/F0084 (Core MR !938) is on production - never ship
  // UI before its backend route. Gated by EFFECTIVE keys, not role names.
  const priceBookingId = occurrence.bookingId ?? booking.id ?? null;
  const isPending = isPendingDisplayStatus(occurrence.statusKey);
  // M004-F0678 (review 15371 R1 of !400, WebUI #108): edit-in-place — the
  // capability the old /app/bookingsAdmin/:id Edit-Booking modal gave an org
  // admin, now in the drawer (UI-4: no modal, no navigation). Offered only to a
  // HOST that opted in by passing `onBookingSaved` (the Studio tab: it refetches,
  // so the drawer re-renders from server truth, never an optimistic patch); the
  // Calendar tab does not, so a rule-expanded occurrence can never be edited as
  // if it were its series master. Gated on the permission key, never a role name,
  // and on the modal's own rule: a cancelled or rejected booking is not editable.
  const editableBookingId = occurrence.bookingId ?? booking.id ?? null;
  const canEditDetails = typeof onBookingSaved === 'function'
    && permissions?.bookings_manage_all === true
    && editableBookingId != null
    && booking.status !== 'cancelled'
    && booking.status !== 'rejected';

  const runGuestCorrection = async (email) => {
    setGuestCorrectionError('');
    setGuestCorrectionSaving(true);
    try {
      const bookingId = occurrence.bookingId ?? booking.id;
      await bookingApi.setBillingCustomerFromGuest(bookingId, email);
      const readback = await bookingApi.getBooking(bookingId);
      const refreshedBooking = readback?.data?.data;
      if (!refreshedBooking || String(refreshedBooking.id) !== String(bookingId)
        || refreshedBooking.ledger_customer_id == null) {
        throw new Error(calendarGuestUnresolved
          ? 'Core has not confirmed an active billing customer for this organisation. The warning remains; try again.'
          : 'Core has not confirmed an active billing customer for this organisation. The current billing customer remains displayed; try again.');
      }
      if (typeof onBillingCustomerResolved !== 'function') {
        throw new Error(calendarGuestUnresolved
          ? 'Could not refresh this booking from Core. The warning remains; reload and try again.'
          : 'Could not refresh this booking from Core. The current billing customer remains displayed; reload and try again.');
      }
      onBillingCustomerResolved(bookingId, refreshedBooking);
      setGuestCorrectionOpen(false);
    } catch (err) {
      const challenge = getStepUpChallengeFromError(err);
      if (challenge?.id) {
        // Replay the same booking and exact submitted email after SAL4.
        guestCorrectionReplay.current = () => runGuestCorrection(email);
        setGuestCorrectionStepUp(challenge);
        return;
      }
      const serverMessage = err?.response?.data?.message || err.message || 'Core could not confirm this guest.';
      setGuestCorrectionError(calendarGuestUnresolved
        ? `${serverMessage} The warning remains; try again.`
        : `${serverMessage} The current billing customer remains displayed until Core confirms a change.`);
    } finally {
      setGuestCorrectionSaving(false);
    }
  };

  const handleGuestCorrection = (event) => {
    event.preventDefault();
    const email = guestEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setGuestCorrectionError('Enter a valid guest email. Core must confirm the active organisation member before it can become the billing customer.');
      return;
    }
    runGuestCorrection(email);
  };

  const openEditForm = () => {
    // Seeded from the CURRENT server row on every open. M004-F0622:
    // formatDateForLocalInput builds datetime-local values from LOCAL
    // components, so an untouched save round-trips losslessly.
    setEditForm({
      title: booking.title || '',
      description: booking.description || '',
      start_time: formatDateForLocalInput(booking.start_time),
      end_time: formatDateForLocalInput(booking.end_time),
    });
    setEditError('');
    setEditSaved(false);
    setEditing(true);
  };

  const closeEditForm = () => {
    setEditing(false);
    setEditForm(null);
    setEditError('');
  };

  const handleEditSave = async () => {
    if (!editForm) return;
    setEditSaving(true);
    setEditError('');
    try {
      await bookingApi.updateBooking(editableBookingId, {
        title: editForm.title,
        description: editForm.description,
        // new Date() parses a naive datetime-local string as LOCAL time, paired
        // with formatDateForLocalInput above (M004-F0622) — never a UTC-digit
        // formatter, or the round trip shifts every time.
        start_time: editForm.start_time ? new Date(editForm.start_time).toISOString() : undefined,
        end_time: editForm.end_time ? new Date(editForm.end_time).toISOString() : undefined,
      });
      setEditing(false);
      setEditForm(null);
      setEditSaved(true);
      await onBookingSaved();
    } catch (err) {
      setEditError(err?.response?.data?.message || 'Failed to update the booking. Please try again.');
    } finally {
      setEditSaving(false);
    }
  };
  const handOffKey = `${priceBookingId}:${occurrence.start || ''}`;
  const priceHandOffId = priceHandOff.key === handOffKey ? priceHandOff.id : '';
  const showPriceSection = isBookingPriceSectionBackendAvailable()
    && Boolean(organisationId) && priceBookingId != null
    && permissions?.pricing_plans_view === true;

  return (
    <div className="bkh-drawer-backdrop" role="presentation" {...drawerDismissHandlers}>
      <aside
        className="bkh-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bkh-drawer-title"
        data-testid={BOOKING_DRAWER_TESTID}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="bkh-drawer-header">
          <div>
            <h2 id="bkh-drawer-title">{occurrence.title}</h2>
            <p>{occurrence.roomName}</p>
            {/* M004-F0712 S3: from the booking's own hand_off_id when a read carries one, else the id the Price read reports. */}
            <TxChip handOffId={booking.hand_off_id || priceHandOffId} txId={transaction?.tx_id} orgId={organisationId} customerUserId={booking.user_id ?? undefined} expected={Boolean(organisationId)} />
          </div>
          <button type="button" className="btn btn-outline" onClick={onClose}>Close</button>
        </div>

        {(() => {
          const bookingBody = (
            <>
        {calendarGuestUnresolved ? (
          <section className="bkh-guest-attention" role="alert" data-testid="bkh-guest-attention">
            <h3>Needs attention: add or correct the guest email</h3>
            <p>{projectionAvailable
              ? 'The Core billing customer is unresolved. This sets the structured guest in Core; it does not edit the Google event.'
              : 'Core has not returned its canonical customer projection. Refresh after the Core update before making a correction.'}</p>
          </section>
        ) : null}
        {calendarGuestEligible && booking.ledger_customer_id != null && canCorrectGuest ? (
          <p className="bkh-guest-correction-entry">
            <button type="button" className="btn btn-outline btn-sm" aria-expanded={guestCorrectionOpen} onClick={() => setGuestCorrectionOpen((open) => !open)}>
              {guestCorrectionOpen ? 'Close guest correction' : 'Correct guest billing identity'}
            </button>
          </p>
        ) : null}
        {showGuestCorrectionForm ? (
          <section className="bkh-guest-correction" aria-labelledby="bkh-guest-correction-title" data-testid="bkh-guest-correction">
            {!calendarGuestUnresolved ? <h3 id="bkh-guest-correction-title">Correct guest billing identity</h3> : null}
            <p>Enter the exact current guest email. Core will confirm and link the active billing customer for this organisation. This updates Core only; it does not edit the Google event.</p>
            <form onSubmit={handleGuestCorrection} data-testid="bkh-guest-correction-form">
              <label htmlFor="bkh-guest-correction-email">Guest email</label>
              <input id="bkh-guest-correction-email" type="email" autoComplete="email" required value={guestEmail} onChange={(event) => setGuestEmail(event.target.value)} />
              <button type="submit" className="btn btn-primary btn-sm" disabled={guestCorrectionSaving || Boolean(guestCorrectionStepUp)}>
                {guestCorrectionSaving ? 'Saving in Core…' : 'Set Core guest and resolve billing customer'}
              </button>
            </form>
            {guestCorrectionError ? <p role="alert" data-testid="bkh-guest-correction-error">{guestCorrectionError}</p> : null}
          </section>
        ) : null}
        {calendarGuestUnresolved && !canCorrectGuest && projectionAvailable ? <p>You need booking management access to correct this guest.</p> : null}
        <dl className="bkh-drawer-list">
          <div>
            <dt>Status</dt>
            <dd>
              <span className={occurrence.statusBadgeClass || 'status-badge'}>{occurrence.statusText}</span>
              {occurrence.isException ? (
                <span className="bkh-drawer-exception-note">
                  {' '}— this date differs from the series (individually changed/cancelled)
                </span>
              ) : null}
            </dd>
          </div>
          <div>
            <dt>When</dt>
            <dd>{timeLabel}</dd>
          </div>
          <div>
            <dt>Room</dt>
            <dd>{occurrence.roomName}</dd>
          </div>
          <div>
            <dt>Requester</dt>
            <dd data-testid="bkh-drawer-requester">{requesterNameOf(booking)}</dd>
          </div>
          {booking.source === 'google_calendar' && booking.ledger_customer_id != null ? (
            <div>
              <dt>Billing customer</dt>
              <dd data-testid="bkh-drawer-billing-customer">{booking.ledger_customer_email || `Core member #${booking.ledger_customer_id}`}</dd>
            </div>
          ) : null}
          {booking.description ? (
            <div>
              <dt>Description</dt>
              <dd data-testid="bkh-drawer-description">{booking.description}</dd>
            </div>
          ) : null}
          {occurrence.seriesPattern ? (
            <div>
              <dt>Series</dt>
              <dd>{occurrence.seriesPattern}</dd>
            </div>
          ) : null}
          {booking.sync_status === 'error' ? (
            <div>
              <dt>Calendar sync</dt>
              <dd className="bkh-card-sync-error">Sync error — this booking failed to reach the calendar.</dd>
            </div>
          ) : null}
        </dl>

        {canEditDetails && !editing ? (
          <p className="bkh-drawer-edit">
            <button
              type="button"
              className="btn btn-outline btn-sm"
              data-testid={EDIT_TOGGLE_TESTID}
              onClick={openEditForm}
            >
              Edit details
            </button>
          </p>
        ) : null}
        {editSaved && !editing ? (
          <p className="bkh-drawer-edit" data-testid="bkh-drawer-edit-saved" role="status">Booking updated.</p>
        ) : null}
        {canEditDetails && editing && editForm ? (
          <form
            className="bkh-drawer-edit-form"
            data-testid={EDIT_FORM_TESTID}
            onSubmit={(event) => { event.preventDefault(); handleEditSave(); }}
          >
            <div className="form-group">
              <label htmlFor="bkh_drawer_edit_title">Title</label>
              <input
                id="bkh_drawer_edit_title"
                type="text"
                data-testid="bkh-drawer-edit-title"
                value={editForm.title}
                onChange={(event) => setEditForm((prev) => ({ ...prev, title: event.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="bkh_drawer_edit_description">Description</label>
              <textarea
                id="bkh_drawer_edit_description"
                rows="2"
                data-testid="bkh-drawer-edit-description"
                value={editForm.description}
                onChange={(event) => setEditForm((prev) => ({ ...prev, description: event.target.value }))}
              />
            </div>
            <div className="form-group">
              <label htmlFor="bkh_drawer_edit_start">Starts</label>
              <input
                id="bkh_drawer_edit_start"
                type="datetime-local"
                data-testid="bkh-drawer-edit-start"
                value={editForm.start_time}
                onChange={(event) => setEditForm((prev) => ({ ...prev, start_time: event.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="bkh_drawer_edit_end">Ends</label>
              <input
                id="bkh_drawer_edit_end"
                type="datetime-local"
                data-testid="bkh-drawer-edit-end"
                value={editForm.end_time}
                onChange={(event) => setEditForm((prev) => ({ ...prev, end_time: event.target.value }))}
                required
              />
            </div>
            {editError ? (
              <p className="bkh-error" role="alert" data-testid="bkh-drawer-edit-error">{editError}</p>
            ) : null}
            <div className="bkh-drawer-edit-actions">
              <button type="button" className="btn btn-outline btn-sm" data-testid="bkh-drawer-edit-cancel" onClick={closeEditForm}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary btn-sm" data-testid="bkh-drawer-edit-save" disabled={editSaving}>
                {editSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        ) : null}

        {/* M004-F0678: approve / decline (host-supplied, permission-gated by the
            host) sit above the price so job 1 reads top to bottom. The old
            "Edit booking" link is gone: the drawer itself now edits (above). */}
        {actionsSlot ? (
          <div className="bkh-drawer-actions" data-testid="bkh-drawer-actions">{actionsSlot}</div>
        ) : null}

        {showPriceSection ? (
          <React.Suspense fallback={<p data-testid="price-loading">Loading price&hellip;</p>}>
            <BookingPriceSection
              key={`${priceBookingId}:${occurrence.statusKey || ''}`}
              holdReason={isPending ? PENDING_PRICE_HOLD : ''}
              organisationId={organisationId}
              bookingId={priceBookingId}
              isSeries={Boolean(occurrence.seriesKey || occurrence.seriesBookingId)}
              occurrenceDate={occurrence.start ? occurrenceDateOf(occurrence.start) : null}
              canView
              canReprice={permissions?.pricing_booking_reprice === true}
              handOffId={booking.hand_off_id || null}
              customerUserId={booking.user_id ?? null}
              onLedgerHandOff={(id) => setPriceHandOff((prev) => (prev.key === handOffKey && prev.id === id ? prev : { key: handOffKey, id }))}
            />
          </React.Suspense>
        ) : null}

        {showPlanSlot && !showPriceSection ? (
          <section
            className="bkh-drawer-plan-slot"
            data-testid={PLAN_SLOT_TESTID}
            data-slot="plan-selector"
            aria-label="Price plan"
          >
            <h3>Price plan</h3>
            {planSelectorSlot}
          </section>
        ) : null}
        <StepUpDialog
          challenge={guestCorrectionStepUp}
          title="Verify guest billing correction"
          description="Confirm your identity before Core changes the billing customer."
          onComplete={() => {
            setGuestCorrectionStepUp(null);
            const replay = guestCorrectionReplay.current;
            guestCorrectionReplay.current = null;
            replay?.();
          }}
          onCancel={() => {
            setGuestCorrectionStepUp(null);
            guestCorrectionReplay.current = null;
            setGuestCorrectionError('Verification was cancelled — the guest billing identity was not changed.');
          }}
        />
            </>
          );
          if (!hasTx) return bookingBody;
          return (
            <BookingDrawerTabs
              selected={tab}
              onSelect={selectTab}
              panels={{
                booking: transaction.bookingUnavailable ? (
                  <p data-testid="bkh-drawer-booking-unavailable">The booking record for this transaction isn&apos;t available here.</p>
                ) : bookingBody,
                history: <BookingTxHistoryTab read={txRead} />,
                billing: <BookingTxBillingTab read={txRead} />,
              }}
            />
          );
        })()}
      </aside>
    </div>
  );
};

export default BookingDrawer;
